import { ExternalLink } from 'lucide-react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui';
import { cn } from '@/lib/cn';
import type { SourceBadge as SourceBadgeShape } from '@/types/api';

interface SourceBadgeProps {
  badge: SourceBadgeShape;
  /** Internal users get full popover; externals see a UUID-only chip. */
  interactive: boolean;
  index: number;
}

/**
 * Source attribution chip rendered next to assistant claims.
 *
 * Internal users see the full popover with URL + dates + agent_version;
 * external clients see only the UUID short prefix (no interactivity).
 */
export function SourceBadge({ badge, interactive, index }: SourceBadgeProps) {
  const shortId = badge.source_id.slice(0, 8);

  if (!interactive) {
    return (
      <span
        className="mx-0.5 inline-flex items-center rounded bg-slate-100 px-1 py-0.5 text-badge font-mono text-slate-700"
        aria-label={`Source ${shortId}`}
      >
        [{index + 1}]
      </span>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'mx-0.5 inline-flex items-center rounded bg-accent/10 px-1 py-0.5 text-badge font-mono text-accent-700 hover:bg-accent/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
          )}
          aria-label={`Source ${shortId}`}
        >
          [{index + 1}]
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-2">
            <span className="text-h4 text-brand-900">Source attribution</span>
            <span className="font-mono text-small text-slate-500">{shortId}</span>
          </div>
          {badge.source_url && (
            <a
              href={badge.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-small text-accent-700 hover:text-accent"
            >
              <ExternalLink className="h-3 w-3" aria-hidden />
              View original source
            </a>
          )}
          <dl className="grid grid-cols-2 gap-1 text-small text-slate-600">
            {badge.publication_date && (
              <>
                <dt className="text-slate-500">Published</dt>
                <dd>{badge.publication_date}</dd>
              </>
            )}
            {badge.ingestion_timestamp && (
              <>
                <dt className="text-slate-500">Ingested</dt>
                <dd>{badge.ingestion_timestamp.slice(0, 10)}</dd>
              </>
            )}
            {badge.agent_version && (
              <>
                <dt className="text-slate-500">Agent</dt>
                <dd className="font-mono">{badge.agent_version}</dd>
              </>
            )}
            {badge.confidence_score != null && (
              <>
                <dt className="text-slate-500">Confidence</dt>
                <dd>{(badge.confidence_score * 100).toFixed(0)}%</dd>
              </>
            )}
          </dl>
        </div>
      </PopoverContent>
    </Popover>
  );
}
